/**
 * Comment decoration feed.
 *
 * `commentDecorationPlugin` is deliberately store-blind: it paints whatever it
 * is handed. This hook is the hand — it watches the comments store for the
 * artifact on screen and pushes `{ comments, focusedCommentId }` into the editor
 * as a metadata transaction. Without it the plugin renders nothing.
 *
 * Two things it must not do:
 *
 * - **Dispatch on no news.** The 30 s poll replaces `byTargetId[artifactId]`
 *   with a fresh array every tick even when the contents are identical, and each
 *   dispatch costs a full re-resolve of every anchor. The change key is a
 *   content signature over exactly the fields a highlight depends on, so an
 *   unchanged list is a no-op.
 * - **Dirty the draft.** The transaction changes no content and is flagged with
 *   {@link COMMENT_DECORATION_META} (which `DraftEditor.onUpdate` skips) and
 *   `addToHistory: false`, so it can neither trigger autosave nor land in the
 *   undo stack.
 */

import { useEffect, useMemo, useRef } from 'react'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import { useCommentsStore } from '@/stores/comments-store'
import type { Comment } from '@/lib/comments-api'
import {
  COMMENT_DECORATION_META,
  commentDecorationPluginKey,
  type CommentDecorationComment,
  type CommentDecorationInput,
} from '@/components/drafting/commentDecorationPlugin'

/**
 * The editor surface the feed touches. A TipTap `Editor` satisfies it — typing
 * it structurally keeps the hook testable without a full editor harness.
 */
export interface CommentDecorationFeedTarget {
  state: EditorState
  view: { dispatch: (tr: Transaction) => void }
  isDestroyed: boolean
}

const NO_COMMENTS: Comment[] = []

/**
 * Everything that can change a highlight: which comments exist, whether they are
 * still open, where they point, and which one is focused. Body edits, replies
 * and unread bookkeeping deliberately do not appear — they never move a
 * highlight, and including them would re-resolve every anchor on every reply.
 */
function changeKey(comments: Comment[], focusedCommentId: string | null): string {
  const parts = comments.map(
    (c) =>
      `${c.id}:${c.status}:${c.sectionId ?? ''}:${c.startPos ?? ''}:${c.endPos ?? ''}:${c.quotedText ?? ''}`,
  )
  return `${parts.join('|')}#${focusedCommentId ?? ''}`
}

function toDecorationComment(comment: Comment): CommentDecorationComment {
  return {
    id: comment.id,
    sectionId: comment.sectionId,
    startPos: comment.startPos,
    endPos: comment.endPos,
    quotedText: comment.quotedText,
    status: comment.status,
  }
}

export function useCommentDecorationFeed(
  editor: CommentDecorationFeedTarget | null,
  artifactId: string,
): void {
  const comments = useCommentsStore((s) => s.byTargetId[artifactId]) ?? NO_COMMENTS
  const focusedCommentId = useCommentsStore((s) => s.focusedCommentId)

  const key = useMemo(
    () => changeKey(comments, focusedCommentId),
    [comments, focusedCommentId],
  )

  // Read through refs so the effect can depend on the change key alone — a poll
  // that returns an identical list must not cost a re-resolve.
  const inputRef = useRef<CommentDecorationInput>({ comments: [], focusedCommentId: null })
  inputRef.current = {
    comments: comments.map(toDecorationComment),
    focusedCommentId,
  }

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.view.dispatch(
      editor.state.tr
        .setMeta(commentDecorationPluginKey, inputRef.current)
        .setMeta(COMMENT_DECORATION_META, true)
        .setMeta('addToHistory', false),
    )
    // `key` is the content signature of `inputRef.current`; `editor` changes
    // when the editor instance is rebuilt (mode/section switch), which resets
    // the plugin state and so needs a re-feed.
  }, [editor, key])
}
